// Exportação do Cronograma de Coleta/Devolução — mesmo padrão de PDF / Imagem
// (JPEG) / Planilha (CSV) da Planilha de Fechamento (`_planilha.ts`).

import {
  DIA_SEMANA_LABEL,
  DIAS_DISPLAY,
  rotuloEventoCronograma,
  type GradeCronograma,
} from "../../domain/cronograma.ts";
import { formatData, hojeISO } from "../../domain/datas.ts";
import {
  ALTURA_RODAPE_MARCA,
  alturaCabecalhoFolha,
  baixarCanvasJpeg,
  baixarTexto,
  type CabecalhoFolha,
  carregarLogo,
  cor,
  cortar,
  desenharCabecalhoFolha,
  desenharRodapeMarca,
  fonteDoc,
  imprimirCanvas,
  nomeArquivo,
} from "./_folha-canvas.ts";

export type FormatoExportCronograma = "pdf" | "jpeg" | "csv";

interface RodapeMarca {
  slogan: string;
  whatsapp: string;
}

const TITULO = "Cronograma de Coleta/Devolução";

export async function exportarCronograma(
  formato: FormatoExportCronograma,
  grade: GradeCronograma,
  rodape: RodapeMarca,
): Promise<void> {
  if (formato === "csv") {
    baixarCsv(grade);
    return;
  }
  const cv = await gerarCanvas(grade, rodape);
  if (formato === "jpeg") {
    baixarCanvasJpeg(cv, nomeArquivo("cronograma-coleta-devolucao", null, hojeISO(), "jpg"));
  } else {
    imprimirCanvas(cv, nomeArquivo("cronograma-coleta-devolucao", null, hojeISO()));
  }
}

// -------------------------------------------------------------------- CSV --

/** Escapa um campo para CSV com `;` — aspas em volta se tiver `;`, `"` ou quebra de linha. */
function campoCsv(s: string): string {
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function baixarCsv(grade: GradeCronograma): void {
  const linha = (campos: readonly string[]): string => campos.map(campoCsv).join(";");

  const linhas: string[] = [
    TITULO,
    linha(["Horário", ...DIAS_DISPLAY.map((d) => DIA_SEMANA_LABEL[d] ?? "?")]),
  ];
  for (const l of grade.linhas) {
    linhas.push(
      linha([
        l.hora,
        ...l.celulas.map((c) => c.eventos.map(rotuloEventoCronograma).join(" / ")),
      ]),
    );
  }
  if (grade.foraDoHorario.length > 0) {
    linhas.push("", `Fora do horário exibido (antes de ${String(grade.horaInicial).padStart(2, "0")}:00 ou depois de ${String(grade.horaFinal).padStart(2, "0")}:00)`);
    linhas.push(linha(["Dia", "Horário", "Evento"]));
    for (const e of grade.foraDoHorario) {
      linhas.push(linha([DIA_SEMANA_LABEL[e.dia] ?? "?", e.hora, rotuloEventoCronograma(e)]));
    }
  }
  baixarTexto(nomeArquivo("cronograma-coleta-devolucao", null, hojeISO(), "csv"), linhas.join("\r\n"));
}

// ---------------------------------------------------------------- canvas --


async function gerarCanvas(
  grade: GradeCronograma,
  rodape: RodapeMarca,
): Promise<HTMLCanvasElement> {
  const logo = await carregarLogo();

  const PAD = 24;
  const TIME_COL = 66;
  const DAY_COL = 150;
  const HEAD_H = 28;
  const CHIP_H = 17;
  const MIN_ROW_H = 24;
  const W = PAD * 2 + TIME_COL + DAY_COL * 7;

  const rowHs = grade.linhas.map((l) => {
    const maxEntradas = Math.max(0, ...l.celulas.map((c) => c.eventos.length));
    return Math.max(MIN_ROW_H, 8 + maxEntradas * CHIP_H);
  });
  const tabelaH = HEAD_H + rowHs.reduce((a, b) => a + b, 0);

  const cab: CabecalhoFolha = {
    titulo: "CRONOGRAMA DE COLETA/DEVOLUÇÃO",
    subtitulo: `Data: ${formatData(hojeISO())}`,
  };
  const hCabecalho = alturaCabecalhoFolha(false);
  const hRodape = ALTURA_RODAPE_MARCA;
  const yTabela = hCabecalho + 14;
  const foraH =
    grade.foraDoHorario.length > 0 ? 34 + grade.foraDoHorario.length * 14 : 0;
  const H = yTabela + tabelaH + PAD + foraH + hRodape;

  const cv = document.createElement("canvas");
  const S = 2;
  cv.width = W * S;
  cv.height = H * S;
  const ctx = cv.getContext("2d");
  if (!ctx) return cv;
  ctx.scale(S, S);
  ctx.fillStyle = cor("surface");
  ctx.fillRect(0, 0, W, H);

  desenharCabecalhoFolha(ctx, W, logo, cab);

  // --- grade ---
  let y = yTabela;
  ctx.strokeStyle = cor("border");
  ctx.lineWidth = 1;

  // cabeçalho: Horário + os 7 dias
  ctx.fillStyle = cor("surface-alt");
  ctx.fillRect(PAD, y, W - PAD * 2, HEAD_H);
  ctx.font = fonteDoc("corpo", "700");
  ctx.fillStyle = cor("muted");
  ctx.textAlign = "left";
  ctx.fillText("HORÁRIO", PAD + 8, y + HEAD_H / 2 + 3);
  ctx.textAlign = "center";
  DIAS_DISPLAY.forEach((dia, i) => {
    const x = PAD + TIME_COL + DAY_COL * i;
    ctx.fillText(
      (DIA_SEMANA_LABEL[dia] ?? "?").toUpperCase(),
      x + DAY_COL / 2,
      y + HEAD_H / 2 + 3,
    );
  });
  ctx.strokeRect(PAD, y, W - PAD * 2, HEAD_H);
  y += HEAD_H;

  grade.linhas.forEach((linha, i) => {
    const rowH = rowHs[i]!;
    if (i % 2 === 1) {
      ctx.fillStyle = cor("surface-alt");
      ctx.fillRect(PAD, y, W - PAD * 2, rowH);
    }
    ctx.strokeStyle = cor("border");
    ctx.strokeRect(PAD, y, TIME_COL, rowH);
    ctx.font = fonteDoc("corpo");
    ctx.fillStyle = cor("muted");
    ctx.textAlign = "right";
    ctx.fillText(linha.hora, PAD + TIME_COL - 6, y + 15);

    linha.celulas.forEach((cel, c) => {
      const x = PAD + TIME_COL + DAY_COL * c;
      ctx.strokeRect(x, y, DAY_COL, rowH);
      let cy = y + 6;
      for (const e of cel.eventos) {
        const bg = e.tipo === "coleta" ? cor("tint-danger") : cor("tint-ok");
        const txt = e.tipo === "coleta" ? cor("danger") : cor("ok");
        const chipW = DAY_COL - 10;
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.roundRect(x + 5, cy, chipW, CHIP_H - 3, 4);
        ctx.fill();
        ctx.fillStyle = txt;
        ctx.font = fonteDoc("chip", "700");
        ctx.textAlign = "left";
        ctx.fillText(cortar(ctx, rotuloEventoCronograma(e), chipW - 10), x + 10, cy + 10);
        cy += CHIP_H;
      }
    });
    y += rowH;
  });

  // --- fora do horário ---
  if (grade.foraDoHorario.length > 0) {
    y += 20;
    ctx.textAlign = "left";
    ctx.fillStyle = cor("ink");
    ctx.font = fonteDoc("corpo", "700");
    ctx.fillText(`FORA DO HORÁRIO EXIBIDO (ANTES DE ${String(grade.horaInicial).padStart(2, "0")}:00 OU DEPOIS DE ${String(grade.horaFinal).padStart(2, "0")}:00)`, PAD, y);
    y += 14;
    ctx.font = fonteDoc("chip");
    ctx.fillStyle = cor("muted");
    for (const e of grade.foraDoHorario) {
      ctx.fillText(
        cortar(ctx, `${DIA_SEMANA_LABEL[e.dia] ?? "?"} ${e.hora} — ${rotuloEventoCronograma(e)}`, W - PAD * 2),
        PAD,
        y,
      );
      y += 14;
    }
  }

  desenharRodapeMarca(ctx, W, H - hRodape, rodape);
  return cv;
}
